import { ActivatedRoute } from '@angular/router';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';

import { AcceptTestSignatoriesUseCase, GetClientUseCase, GetTestDocumentsUseCase, UploadTestDocumentsUseCase } from '../../../../core';
import { BasePage } from '../../base.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { FullClient, Id } from '../../../domain/data';
import { LocalStorage } from '../../../client/helpers/local-storage';
import { ParentEvents } from '../../../utils/parent-events.service';
import { RepeatTestPage } from '../repeat-test.page';
import { validateFilesize } from '../../../validators/filesize/validator';
import { validateFiletype } from '../../../validators/filetype/validator';

@Component({
    templateUrl: './repeat-test-sign-handwriting.page.html',
    styleUrls: ['./repeat-test-sign-handwriting.page.scss'],
})
export class RepeatTestSignHandwritingPage extends BasePage implements AfterViewInit, OnInit {
    public client: FullClient;

    public canUpload = false;
    public canAccept = false;
    public documentError: string = null;
    public isSuitability = false;

    @ViewChild('signedDocument')
    signedDocument: ElementRef;

    constructor(
        parentEvents: ParentEvents,
        repeatTestPage: RepeatTestPage,
        protected acceptTestSignatoriesUseCase: AcceptTestSignatoriesUseCase,
        protected dialog: MatDialog,
        protected getClientUseCase: GetClientUseCase,
        protected getTestDocumentsUseCase: GetTestDocumentsUseCase,
        protected localStorage: LocalStorage,
        protected route: ActivatedRoute,
        protected translator: TranslateService,
        protected uploadTestDocumentsUseCase: UploadTestDocumentsUseCase
    ) {
        super(parentEvents);
        repeatTestPage.hideBackButton();
    }

    ngOnInit() {
        this.getClient();

        this.route.queryParams.subscribe((params) => {
            this.isSuitability = params.suitability === '1';
        });
    }

    protected getClient() {
        if (this.localStorage.clientCode) {
            this.getClientUseCase.bind({ code: Id.fromString(this.localStorage.clientCode) }).subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.client = transaction.data as FullClient;
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog, {
                        data: { text: transaction.error },
                    });
                }
            });
        } else {
            this.parentEvents.cancel();
        }
    }

    onDownload() {
        this.getTestDocumentsUseCase.bind({ client: this.client }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.canUpload = true;
                FileSaver.saveAs(transaction.data, 'download.zip');
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onUpload() {
        if (this.signedDocument) {
            this.signedDocument.nativeElement.value = null;
            this.signedDocument.nativeElement.click();
        }
    }

    onFileChange(event: Event) {
        this.documentError = null;

        if (event.target instanceof HTMLInputElement) {
            let validateOk = true;

            for (let i = 0; i < event.target.files.length; i++) {
                const file = event.target.files.item(i);

                if (!validateFilesize(file, 100 * 1024 * 1024)) {
                    this.translator.get('form.validation.filesize', { size: 100 }).subscribe((msg) => (this.documentError = msg));
                    validateOk = false;
                } else if (!validateFiletype(file, ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'])) {
                    this.translator.get('form.validation.filetype').subscribe((msg) => (this.documentError = msg));
                    validateOk = false;
                }
                if (!validateOk) {
                    break;
                }
            }

            if (validateOk) {
                this.upload(Array.from(event.target.files));
            }
        }
    }

    private upload(files: File[]) {
        this.uploadTestDocumentsUseCase.bind({ client: this.client, documents: files }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.canAccept = true;
                // TODO: feedback
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { type: 'info', text: transaction.error },
                });
            }
        });
    }

    onValidate() {
        this.acceptTestSignatoriesUseCase.bind({ client: this.client }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.parentEvents.cancel();
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }
}
